import { NavLink } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';

interface NavEntry {
  to: string;
  label: string;
  icon: string;
  section: 'Overview' | 'Modules' | 'AI Engine' | 'Tools';
  badge?: string;
  badgeColor?: 'amber' | 'saffron';
}

const NAV: NavEntry[] = [
  { to: '/',                label: "Director's Cockpit", section: 'Overview', icon: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z' },
  { to: '/actions-due',     label: 'Action Due',         section: 'Overview', icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11', badge: '4', badgeColor: 'amber' },
  { to: '/beneficiaries',   label: 'Beneficiaries',      section: 'Modules', icon: 'M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3M3 12h18l-2 7H5l-2-7z', badge: '13k' },
  { to: '/poultry',         label: 'Poultry Approval',   section: 'Modules', icon: 'M12 2a10 10 0 1 0 0 20M12 8v4l2 2', badge: '23', badgeColor: 'amber' },
  { to: '/maitri',          label: 'AIM-MAITRI',         section: 'Modules', icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
  { to: '/mela',            label: 'Pashu Melas',        section: 'Modules', icon: 'M3 4h18v18H3zM16 2v4M8 2v4M3 10h18', badge: '89' },
  { to: '/invest',          label: 'Invest UP',          section: 'Modules', icon: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' },
  { to: '/ai-analytics',    label: 'AI Analytics',       section: 'AI Engine', icon: 'M22 7l-8.5 8.5-5-5L2 17M16 7h6v6' },
  { to: '/recommendations', label: 'AI Recommendations', section: 'AI Engine', icon: 'M12 3l1.91 5.27L19 9.27l-4 4.4 1.2 5.33L12 16.27l-5.2 2.73L8 13.67 4 9.27l5.09-1L12 3z', badge: '4', badgeColor: 'amber' },
  { to: '/reports',         label: 'Reports & Exports',  section: 'Tools', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' },
  { to: '/users',           label: 'User Management',    section: 'Tools', icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
];

const SECTIONS: NavEntry['section'][] = ['Overview', 'Modules', 'AI Engine', 'Tools'];

export function Sidebar() {
  const { user, signOut } = useAuth();
  const initials = (user?.full_name ?? 'NA').split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <aside className="w-60 bg-navy-800 text-white flex-shrink-0 flex flex-col">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-saffron-500 flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-7h6v7" />
            </svg>
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">AHD UP</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Unified CCC</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4 text-sm overflow-y-auto">
        {SECTIONS.map((section) => (
          <div key={section}>
            <div className="px-5 mt-3 mb-2 text-[10px] uppercase tracking-widest text-white/40">{section}</div>
            {NAV.filter((n) => n.section === section).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 transition ${
                    isActive
                      ? 'bg-saffron-500/10 border-l-[3px] border-saffron-500 text-white'
                      : 'text-white/80 hover:bg-white/5'
                  }`
                }
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={n.icon} />
                </svg>
                <span className="flex-1">{n.label}</span>
                {n.badge && (
                  <span
                    className={`text-[10px] px-1.5 rounded font-numeric ${
                      n.badgeColor === 'amber'
                        ? 'bg-amber-500/30 text-amber-200'
                        : 'bg-saffron-500 text-white'
                    }`}
                  >
                    {n.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-saffron-500/20 border border-saffron-500/40 flex items-center justify-center text-saffron-300 font-medium text-sm">
            {initials}
          </div>
          <div className="flex-1 min-w-0 leading-tight">
            <div className="text-sm truncate">{user?.full_name ?? '—'}</div>
            <div className="text-[10px] text-white/50 truncate">
              {user?.designation ?? user?.roles?.[0]?.code ?? 'No role'}
            </div>
          </div>
          <button
            onClick={() => void signOut()}
            className="text-white/50 hover:text-saffron-300 transition"
            aria-label="Sign out"
            title="Sign out"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}
